import { useMemo } from 'react'

import { clsx } from 'clsx'

import {
  Chat,
  ChatStatus,
  ChatType,
  Message,
} from '@tribeplatform/gql-client/types'
import {
  PendingMessage,
  useCachedParticipants,
} from '@tribeplatform/react-sdk/hooks'
import { Avatar } from '@tribeplatform/react-ui-kit/Avatar'

import { MemberAvatarPlaceholder } from '../../../common/components/index.js'
import { useChatContext } from '../../ChatContext/ChatContext.js'
import { isChatToMyself } from '../utils/chat.utils.js'

interface Props {
  chat: Chat
  message: Message | PendingMessage
  isMe: boolean
}

export const ChatMessageAvatar = ({ chat, message, isMe }: Props) => {
  const { data: participants } = useCachedParticipants({ chatId: chat._id })
  const { toggleShowChatDetails } = useChatContext()
  const isTombstone = chat.status === ChatStatus.Tombstone
  const participant = useMemo(() => {
    if (chat.currentParticipant?._id === message.authorParticipantId) {
      return chat.currentParticipant
    }

    if (chat.type === ChatType.Direct) {
      if (isChatToMyself(chat)) {
        return chat.currentParticipant
      }

      return chat.otherParticipant?._id === message.authorParticipantId
        ? chat.otherParticipant
        : chat.currentParticipant
    }
    return participants[message.authorParticipantId]
  }, [participants, chat, message.authorParticipantId])
  const showDetailsOnClick = chat.type === ChatType.Group || !isMe

  if (isTombstone) {
    return <MemberAvatarPlaceholder size="2.5x" name="" />
  }

  if (!participant) {
    return <div className="rounded-base h-10 w-10 bg-skeleton" />
  }

  return (
    <div
      onClick={() =>
        showDetailsOnClick ? toggleShowChatDetails(participant) : undefined
      }
    >
      <Avatar
        size="2.5x"
        className={clsx(
          'cursor-pointer',
          showDetailsOnClick &&
            'hover:opacity-80 transition-opacity duration-200',
        )}
        name={participant?.name ?? ''}
        src={participant?.avatarUrl ?? ''}
        fallback={MemberAvatarPlaceholder}
      />
    </div>
  )
}
